import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import { AppButton } from 'components/AppButton';
import { InfoCard, WarningCard } from 'components/notification-cards';
import { Text } from 'components/Text';
import { PATHS } from 'config/paths';
import { getTrans } from 'modules/i18n';
import { ModalName, useModal } from 'modules/modal';
import {
  GENERAL_STATUS_MAP,
  GeneralStatus,
  StatusBadge
} from 'modules/status-badge';
import { FeatureFlag, useFeatureToggle } from 'modules/unleash';
import { useWeekDetails } from '../hooks';
import { getAutoCloseLabel, isMaxShiftsReached } from '../utils';
import { AssignmentSummaryCardSkeleton } from './AssignmentSummaryCardSkeleton';
import { ReasonCard } from './ReasonCard';
import {
  AutoCloseLabel,
  ButtonsWrapper,
  SummaryCard,
  SummaryCardContent,
  SummaryCardHeader
} from './styledComponents';
import { WorkedHoursProgressBar } from './WorkedHoursProgressBar';

export const AssignmentSummaryCard = () => {
  const { weekId } = useParams();
  const navigate = useNavigate();
  const { weekDetails, isLoading } = useWeekDetails();
  const { open } = useModal();
  const { isEnabled } = useFeatureToggle(
    FeatureFlag.IS_DEVIATION_DETAILS_PAGE_ENABLED
  );

  if (isLoading) {
    return <AssignmentSummaryCardSkeleton />;
  }

  if (!weekId || !weekDetails) return null;

  const { assignments, reasons } = weekDetails;
  const assignment = assignments[weekId];
  if (!assignment) return null;
  const {
    weekStatus,
    companyName,
    weekAutoClosing,
    reportedReasonMinutes,
    reportedMinutes,
    expectedMinutes,
    reasons: reasonIds,
    shiftTimesManagedByCompany
  } = assignment;

  const autoCloseLabel = getAutoCloseLabel(weekStatus, weekAutoClosing);

  const isWeekOpen = weekStatus === GeneralStatus.OPEN;
  const isMaxShifts = isMaxShiftsReached(assignment?.shifts?.length);

  const isWorkedExactlyAsExpected =
    !reportedReasonMinutes && reportedMinutes === expectedMinutes;

  // Calculate total worked minutes by combining regular reported minutes (shifts)
  // and any additional reported reason minutes (e.g., overtime justifications, time off)
  const actualWorkedMinutes = reportedMinutes + reportedReasonMinutes;
  const isReasonsNotMatchingOverHours =
    reportedMinutes > expectedMinutes &&
    actualWorkedMinutes !== expectedMinutes;

  const isWeekCloseable = () => actualWorkedMinutes === expectedMinutes;

  const handleReasonClick = (id: string) => {
    if (isEnabled) {
      navigate(`${PATHS.reasonDetails}`.replace(':reasonId', id));
    }
  };

  return (
    <SummaryCard>
      <SummaryCardHeader>
        <Text variant="h4">{companyName}</Text>
      </SummaryCardHeader>
      <SummaryCardContent>
        <Box>
          <StatusBadge status={weekStatus} statusMap={GENERAL_STATUS_MAP} />
        </Box>
        <Box>
          <WorkedHoursProgressBar
            reportedMinutes={reportedMinutes}
            expectedMinutes={expectedMinutes}
            reportedReasonMinutes={reportedReasonMinutes}
          />
        </Box>
        {autoCloseLabel && (
          <Box>
            <AutoCloseLabel variant="body2">{autoCloseLabel}</AutoCloseLabel>
          </Box>
        )}
        {shiftTimesManagedByCompany && (
          <Box>
            <InfoCard>
              <Text variant="body2">
                {getTrans(
                  'weekDetailsPage.infoCard.shiftTimesManagedByCompany'
                )}
              </Text>
            </InfoCard>
          </Box>
        )}
        {isWeekOpen && (
          <>
            <ButtonsWrapper>
              {!isWorkedExactlyAsExpected && (
                <AppButton
                  variant={
                    isReasonsNotMatchingOverHours ? 'contained' : 'outlined'
                  }
                  disabled={isWeekCloseable()}
                  color="secondary"
                  onClick={() => open(ModalName.addReason)}
                >
                  {getTrans('weekDetailsPage.button.justifyDeviation')}
                </AppButton>
              )}
              <AppButton
                color="secondary"
                disabled={!isWeekCloseable()}
                onClick={() => open(ModalName.closeWeek)}
              >
                {getTrans('weekDetailsPage.button.finishWeek')}
              </AppButton>
            </ButtonsWrapper>
            {!isWorkedExactlyAsExpected && (
              <Box>
                <InfoCard>
                  <Text variant="body2">
                    {getTrans('weekDetailsPage.infoCard.text')}
                  </Text>
                </InfoCard>
              </Box>
            )}
          </>
        )}
        {isWeekOpen && isMaxShifts && (
          <Box>
            <WarningCard>
              <Text variant="body2">
                {getTrans('weekDetailsPage.warning.maxShifts')}
              </Text>
            </WarningCard>
          </Box>
        )}
        {reasons &&
          reasonIds?.map((id, index) => (
            <ReasonCard
              key={`reason-${id}`}
              reason={reasons[id]}
              isLastInGroup={index === reasonIds.length - 1}
              onClick={() => handleReasonClick(id)}
              testId={`reason-card-${id}`}
            />
          ))}
      </SummaryCardContent>
    </SummaryCard>
  );
};
